import { enableProdMode, LOCALE_ID, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { PLATFORM_ID, isDevMode } from '@angular/core';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { adapterFactory } from 'angular-calendar/date-adapters/moment';
import { CalendarCommonModule, DateAdapter } from 'angular-calendar';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ServiceWorkerModule, provideServiceWorker } from '@angular/service-worker';
import { ToastrModule } from 'ngx-toastr';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClarityModule } from '@clr/angular';
import { provideAnimations } from '@angular/platform-browser/animations';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { APP_BASE_HREF, DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateService, provideTranslateService } from '@ngx-translate/core';
import { OKTA_CONFIG, OktaAuthModule } from '@okta/okta-angular';
import { Logoutinterceptor } from './app/services/LogoutInterceptor/logoutinterceptor';
import OktaAuth from '@okta/okta-auth-js';

//#region Locale
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/nl';
import 'dayjs/locale/ru';
import 'dayjs/locale/pt';
import 'dayjs/locale/tr';

import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localeNl from '@angular/common/locales/nl';
import localeRu from '@angular/common/locales/ru';
import localePt from '@angular/common/locales/pt';
import localeTr from '@angular/common/locales/tr';
//#endregion

import { MrTranslatePipe } from './app/pipes/mr-translate.pipe';
import { urlEnDecodePipe } from './app/pipes/urlEncode.pipe';
import { BackendLocaleDatePipe } from './app/pipes/get-locale-date.pipe';
import { AngularDeviceInformationService } from 'angular-device-information';
import { ObjTypePipe } from './app/pipes/objtype.pipe';
import { DynamicLocaleId } from './app/models/ui/translation/dynamic-locale-id.model';
import appRoutes from './app/app.routes';

// Determine if we're running on HTTPS or localhost
const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const oktaAuth = new OktaAuth({
  issuer: `https://${environment.okta.domain}/oauth2/default`,
  clientId: `${environment.okta.clientId}`,
  redirectUri: window.location.origin + '/login/callback',
  pkce: true,
  scopes: ['openid', 'profile', 'email', 'groups-mrpro'],
  responseType: ['code', 'id_token', 'token', 'refresh_token'],
  responseMode: 'query',
  storageManager: {
    token: {
      // Use localStorage for local development (HTTP), cookies for production (HTTPS)
      storageTypes: isLocalDev ? ['localStorage', 'sessionStorage'] : ['cookie'],
    },
  },
  tokenManager: {
    autoRenew: true,
  },
  cookies: {
    // Only require secure cookies in production (HTTPS)
    secure: !isLocalDev,
    sameSite: isLocalDev ? 'lax' : 'none',
  },
});

registerLocaleData(localeDe);
registerLocaleData(localeEn);
registerLocaleData(localeFr);
registerLocaleData(localeIt);
registerLocaleData(localeNl);
registerLocaleData(localeRu);
registerLocaleData(localePt);
registerLocaleData(localeTr);

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideTranslateService({
      fallbackLang: 'de',
      lang: 'de',
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
      }),
    }),
    importProvidersFrom(
      BrowserModule,
      ClarityModule,
      FormsModule,
      ReactiveFormsModule,
      ToastrModule.forRoot({ positionClass: 'toast-bottom-right' }),
      CalendarCommonModule.forRoot({
        provide: DateAdapter,
        useFactory: () => adapterFactory(dayjs),
      }),
      OktaAuthModule.forRoot({ oktaAuth })
    ),

    // * if Servise has providedIn: "root", there is no need to declare here
    { provide: LOCALE_ID, useClass: DynamicLocaleId, deps: [TranslateService] },
    { provide: HTTP_INTERCEPTORS, useClass: Logoutinterceptor, multi: true },
    { provide: OKTA_CONFIG, useValue: { oktaAuth } },
    { provide: OktaAuth, useValue: oktaAuth },
    { provide: APP_BASE_HREF, useValue: '/' },
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(appRoutes),
    provideAnimations(),

    // * nur Pipes, die Injectable sein sollten (nur in .ts File; .html braucht nur imports: [])
    BackendLocaleDatePipe,
    MrTranslatePipe,
    urlEnDecodePipe,
    DatePipe,
    ObjTypePipe,

    {
      provide: AngularDeviceInformationService,
      useFactory: (platformId: Object) => new AngularDeviceInformationService(platformId),
      deps: [PLATFORM_ID],
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:3000',
    }),
  ],
}).catch(err => console.error(err));
